import { GA_MEASUREMENT_ID, DEBUG } from './config'
import { getUserId } from './auth'

/**
 * TypeScript type definitions for Google Analytics gtag function
 * 
 * These types ensure type safety when calling gtag() throughout the application.
 */
declare global {
  interface Window {
    gtag: (
      command: 'config' | 'set' | 'event',
      targetId: string | Record<string, any>,
      config?: Record<string, any>
    ) => void
    dataLayer: any[]
  }
}

/**
 * Track a page view in Google Analytics
 * 
 * This function sends a page view event to GA4 with the specified URL path.
 * Used for tracking route changes in Next.js App Router (client-side navigation).
 * Includes user_id if user is logged in.
 * 
 * @param url - The URL path to track (e.g., '/dashboard', '/courses?filter=active')
 * 
 * Example:
 *   pageview('/dashboard')
 *   pageview('/courses?filter=active')
 */
export const pageview = (url: string): void => {
  if (typeof window === 'undefined' || !window.gtag || !GA_MEASUREMENT_ID) {
    return
  }

  const userId = getUserId()

  window.gtag('config', GA_MEASUREMENT_ID, {
    page_path: url,
    ...(userId && { user_id: userId })
  })
}

/**
 * Fire a custom event in Google Analytics
 * 
 * This function sends a custom event to GA4 with optional parameters.
 * All event names must use snake_case (e.g., 'login_attempt', 'sign_up_start').
 * Automatically includes user_id if user is logged in (except `course_email_visit`, which omits auto user_id).
 * 
 * Also pushes events to dataLayer for GTM to listen (hybrid approach).
 * This allows marketing tags in GTM to react to product events.
 * 
 * @param name - Event name in snake_case (e.g., 'login_attempt', 'feature_opened')
 * @param params - Optional event parameters (must use snake_case keys, no PII)
 * 
 * @example
 *   event('login_attempt')
 *   event('login_failure', { error_message: 'Invalid credentials' })
 * 
 * @see docs/ANALYTICS_SCHEMA.md for complete event documentation
 */
export const event = (name: string, params?: Record<string, any>): void => {
  if (typeof window === 'undefined') {
    return
  }

  emitEvent(name, buildEventParams(name, params))
}

/** Give up waiting on GA so a pending navigation is never blocked on analytics. */
const EVENT_DELIVERY_TIMEOUT_MS = 600

const buildEventParams = (
  name: string,
  params?: Record<string, any>
): Record<string, any> => {
  const userId = getUserId()

  // Pre-login partner email entry: keep hit free of user_id (see `partnerCourseRewardEventParams` for other funnel events).
  const omitAutoUserId = name === 'course_email_visit'

  // Include user_id in event if available
  return {
    ...params,
    ...(!omitAutoUserId && userId && { user_id: userId }),
  }
}

/**
 * `gtagOnlyParams` carries gtag transport options (`transport_type`, `event_callback`)
 * that must not leak into the dataLayer payload GTM tags read.
 */
const emitEvent = (
  name: string,
  eventParams: Record<string, any>,
  gtagOnlyParams?: Record<string, any>
): void => {
  // Debug logging for interview events
  if (DEBUG && name.startsWith('interview_')) {
    console.log('[GA Event]', name, eventParams)
  }

  // Direct GA4 tracking (existing - keep this)
  if (window.gtag && GA_MEASUREMENT_ID) {
    window.gtag('event', name, { ...eventParams, ...gtagOnlyParams })
  }

  // Also push to dataLayer for GTM (for marketing tags)
  if (window.dataLayer) {
    window.dataLayer.push({
      event: name,
      ...eventParams
    })
  }
}

/**
 * Fire an event and resolve once GA confirms delivery (or the timeout elapses).
 *
 * Use this instead of `event()` when a full-page navigation follows immediately:
 * `window.location` unloads the document and cancels the in-flight hit, so the
 * event never reaches GA. Sends over `beacon` transport and waits on
 * `event_callback` before the caller navigates.
 *
 * @param name - Event name in snake_case
 * @param params - Optional event parameters (must use snake_case keys, no PII)
 *
 * @example
 *   await eventWithDelivery('login_success', { method: 'email_otp' })
 *   window.location.replace('/dashboard')
 */
export const eventWithDelivery = (
  name: string,
  params?: Record<string, any>
): Promise<void> => {
  if (typeof window === 'undefined') {
    return Promise.resolve()
  }

  const eventParams = buildEventParams(name, params)

  // No gtag on the page: nothing to wait for, but GTM should still see the event.
  if (!window.gtag || !GA_MEASUREMENT_ID) {
    emitEvent(name, eventParams)
    return Promise.resolve()
  }

  return new Promise<void>(resolve => {
    let settled = false
    const settle = () => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      resolve()
    }
    const timer = setTimeout(settle, EVENT_DELIVERY_TIMEOUT_MS)

    emitEvent(name, eventParams, {
      transport_type: 'beacon',
      event_callback: settle,
    })
  })
}

/**
 * Track when a user opens/enters a feature
 * 
 * This helper fires a 'feature_opened' event with the feature name.
 * Use this to track when users enter meaningful features (not every page load).
 * 
 * @param featureName - Name of the feature in snake_case (e.g., 'interview', 'interview_analysis')
 * 
 * @example
 *   featureOpened('interview')
 *   featureOpened('interview_analysis')
 * 
 * @see docs/ANALYTICS_SCHEMA.md for complete feature tracking documentation
 */
export const featureOpened = (featureName: string) => {
  event('feature_opened', { feature_name: featureName })
}

/**
 * Track when a user completes a feature
 * 
 * This helper fires a 'feature_completed' event with the feature name.
 * Use this to track when users successfully complete meaningful features.
 * 
 * @param featureName - Name of the feature in snake_case (e.g., 'interview')
 * 
 * @example
 *   featureCompleted('interview')
 * 
 * @see docs/ANALYTICS_SCHEMA.md for complete feature tracking documentation
 */
export const featureCompleted = (featureName: string) => {
  event('feature_completed', { feature_name: featureName })
}

/**
 * Set user ID in Google Analytics
 * 
 * Associates all future events and sessions with the provided user ID.
 * Use this after successful login to link analytics data across sessions.
 * Set to null on logout to stop associating new sessions.
 * 
 * @param userId - Internal user ID (UUID string, not PII) or null to clear
 * 
 * @example
 *   setUserId('123e4567-e89b-12d3-a456-426614174000')
 *   setUserId(null) // On logout
 * 
 * Note: Only internal user IDs (UUIDs) should be sent. Never send PII.
 */
export const setUserId = (userId: string | null): void => {
  if (typeof window === 'undefined' || !window.gtag || !GA_MEASUREMENT_ID) {
    return
  }

  window.gtag('config', GA_MEASUREMENT_ID, {
    user_id: userId || null
  })
}

/**
 * Track interview funnel start
 * 
 * Fires when user clicks "Start Interview" or "Continue" on interview intro page.
 * 
 * @param interviewId - Interview ID (string or number)
 * @param interviewType - Optional interview type (e.g., 'behavioral', 'technical')
 * 
 * @example
 *   interviewFunnelStart('123', 'behavioral')
 */
export const interviewFunnelStart = (interviewId: string | number, interviewType?: string) => {
  event('interview_funnel_start', {
    interview_id: String(interviewId),
    ...(interviewType && { interview_type: interviewType })
  })
}

/**
 * Track when a question is viewed
 * 
 * Fires when a question is displayed to the user.
 * 
 * @param interviewId - Interview ID (string or number)
 * @param questionNumber - Question number (1-indexed)
 * @param questionType - Optional question type (e.g., 'behavioral', 'technical', 'code_completion')
 * 
 * @example
 *   interviewQuestionViewed('123', 1, 'behavioral')
 */
export const interviewQuestionViewed = (
  interviewId: string | number,
  questionNumber: number,
  questionType?: string
) => {
  event('interview_question_viewed', {
    interview_id: String(interviewId),
    question_number: questionNumber,
    ...(questionType && { question_type: questionType })
  })
}

/**
 * Track when an answer is submitted
 * 
 * Fires when user submits an answer to a question.
 * 
 * @param interviewId - Interview ID (string or number)
 * @param questionNumber - Question number (1-indexed)
 * @param answerDurationSeconds - Time spent answering (in seconds)
 * @param hasClarification - Whether user requested clarification
 * 
 * @example
 *   interviewAnswerSubmitted('123', 1, 45, false)
 */
export const interviewAnswerSubmitted = (
  interviewId: string | number,
  questionNumber: number,
  answerDurationSeconds: number,
  hasClarification: boolean = false
) => {
  event('interview_answer_submitted', {
    interview_id: String(interviewId),
    question_number: questionNumber,
    answer_duration_seconds: Math.round(answerDurationSeconds),
    has_clarification: hasClarification
  })
}

/**
 * Track interview submission
 * 
 * Fires when user clicks "Submit Interview" button.
 * 
 * @param interviewId - Interview ID (string or number)
 * @param attemptNumber - Attempt number
 * @param totalQuestions - Total number of questions in interview
 * @param questionsAnswered - Number of questions answered
 * @param totalDurationSeconds - Total interview duration (in seconds)
 * 
 * @example
 *   interviewSubmitted('123', 1, 5, 5, 1800)
 */
export const interviewSubmitted = (
  interviewId: string | number,
  attemptNumber: number,
  totalQuestions: number,
  questionsAnswered: number,
  totalDurationSeconds: number
) => {
  event('interview_submitted', {
    interview_id: String(interviewId),
    attempt_number: attemptNumber,
    total_questions: totalQuestions,
    questions_answered: questionsAnswered,
    total_duration_seconds: Math.round(totalDurationSeconds)
  })
}

/**
 * Track interview results viewed
 * 
 * Fires when user views interview results/analysis page.
 * 
 * @param interviewId - Interview ID (string or number)
 * @param attemptNumber - Attempt number
 * @param overallScore - Overall interview score
 * @param timeToResultsSeconds - Time from submission to results view (in seconds)
 * 
 * @example
 *   interviewResultsViewed('123', 1, 85, 5)
 */
export const interviewResultsViewed = (
  interviewId: string | number,
  attemptNumber: number,
  overallScore: number,
  timeToResultsSeconds?: number
) => {
  event('interview_results_viewed', {
    interview_id: String(interviewId),
    attempt_number: attemptNumber,
    overall_score: Math.round(overallScore),
    ...(timeToResultsSeconds !== undefined && { time_to_results_seconds: Math.round(timeToResultsSeconds) })
  })
}

/**
 * Track page engagement time
 * 
 * Fires when user leaves a page, tracking how long they spent on it.
 * 
 * @param pagePath - The page path (e.g., '/dashboard', '/interview')
 * @param timeSeconds - Time spent on page (in seconds)
 * 
 * @example
 *   trackPageTime('/dashboard', 120)
 */
export const trackPageTime = (pagePath: string, timeSeconds: number) => {
  event('page_time', {
    page_path: pagePath,
    time_seconds: Math.round(timeSeconds)
  })
}

/**
 * Track feature engagement time
 * 
 * Fires when user leaves a feature, tracking how long they engaged with it.
 * 
 * @param featureName - Name of the feature in snake_case (e.g., 'interview', 'interview_analysis')
 * @param timeSeconds - Time spent in feature (in seconds)
 * 
 * @example
 *   trackFeatureEngagement('interview', 1800)
 */
export const trackFeatureEngagement = (featureName: string, timeSeconds: number) => {
  event('feature_engagement', {
    feature_name: featureName,
    time_seconds: Math.round(timeSeconds)
  })
}

/**
 * Track when the course recommendations section enters the viewport.
 *
 * @param source - Where the section lives: 'dashboard' | 'interview_summary'
 */
export const courseSectionViewed = (source: string) => {
  event('course_section_viewed', { source })
}

/**
 * Track when a user clicks the "Interested" button on a course card.
 *
 * @param courseId - Stable course identifier (e.g. 'mastering-agentic-ai')
 * @param courseTitle - Human-readable course title
 * @param source - 'dashboard' | 'interview_summary'
 */
export const courseInterestClicked = (courseId: string, courseTitle: string, source: string) => {
  event('course_interest_clicked', { course_id: courseId, course_title: courseTitle, source })
}

/**
 * Track when course interest is first recorded via the API (res.ok only, not 409).
 * Must not fire when the backend returns 409 (already marked) to prevent duplicate events
 * when the same course appears on both Dashboard and Interview History.
 *
 * @param courseId - Stable course identifier
 * @param source - 'dashboard' | 'interview_history'
 */
export const courseInterestMarked = (courseId: string, source: string) => {
  event('course_interest_marked', { course_id: courseId, source })
}

/**
 * Track when the course platform link is opened in a new tab.
 *
 * @param courseId - Stable course identifier
 * @param courseTitle - Human-readable course title
 * @param source - 'dashboard' | 'interview_summary'
 */
export const courseLinkOpened = (courseId: string, courseTitle: string, source: string) => {
  event('course_link_opened', { course_id: courseId, course_title: courseTitle, source })
}

export interface LabExerciseRecommendationEventParams {
  interviewId: string
  attemptNumber: number
  labExerciseId: string
  matchType: string | null
  /** Skills the service matched; joined with "," (GA params are scalars). */
  matchedSkills: string[]
}

const labExerciseRecommendationEventPayload = (params: LabExerciseRecommendationEventParams) => ({
  interview_id: params.interviewId,
  attempt_number: params.attemptNumber,
  lab_exercise_id: params.labExerciseId,
  match_type: params.matchType ?? 'unknown',
  matched_skills: params.matchedSkills.join(','),
  source: 'interview_summary',
})

/**
 * Track when the personalised Lab Exercise recommendation card becomes visible.
 * Fired once per interview attempt and exercise per page load.
 */
export const labExerciseRecommendationViewed = (params: LabExerciseRecommendationEventParams) => {
  event('lab_exercise_recommendation_viewed', labExerciseRecommendationEventPayload(params))
}

/**
 * Track when the user activates "Start Lab Exercise" on the recommendation card.
 * This is a click, not a start: the exercise is started on the Lab Exercise platform.
 */
export const labExerciseRecommendationClicked = (params: LabExerciseRecommendationEventParams) => {
  event('lab_exercise_recommendation_clicked', labExerciseRecommendationEventPayload(params))
}

/**
 * Track when a homepage landing section enters the viewport (~40% visible).
 * Fired once per section per page load via TrackedLandingSection.
 *
 * @param sectionId - Stable section slug (e.g. 'hero', 'pricing')
 * @param sectionIndex - 1-based order on the page
 *
 * @see docs/ANALYTICS_SCHEMA.md — Landing page section funnel
 */
export const landingSectionViewed = (sectionId: string, sectionIndex: number) => {
  event('landing_section_viewed', {
    section_id: sectionId,
    section_index: sectionIndex,
    page_path: analyticsPagePath(),
  })
}

/**
 * Track when a free mock interview marketing page section enters the viewport (~40% visible).
 * Separate from homepage `landing_section_viewed` so GA4/GTM can funnel this campaign alone.
 *
 * @param sectionId - Stable section slug (e.g. 'hero', 'what_you_get')
 * @param sectionIndex - 1-based order on the page
 *
 * @see docs/ANALYTICS_SCHEMA.md — Free 15-minute mock interview marketing page
 */
export const freeMockInterviewLandingSectionViewed = (
  sectionId: string,
  sectionIndex: number,
) => {
  event('free_mock_interview_landing_section_viewed', {
    section_id: sectionId,
    section_index: sectionIndex,
    page_path: analyticsPagePath(),
  })
}

/** Homepage CTA surface (also used as `login_entry` when the login modal opens). */
export type LandingCtaSurface =
  | 'hero'
  | 'interview_formats'
  | 'pricing'
  | 'navbar'
  | 'practice_questions'
  // Standalone free mock interview campaign page (app/free-15-minute-mock-interview)
  | 'free_mock_interview_hero'
  | 'free_mock_interview_offer'
  | 'free_mock_interview_final'

export type LandingLoginEntry = LandingCtaSurface | 'unknown'

export type LandingCtaJourney =
  | 'dashboard'
  | 'resume'
  | 'jd'
  | 'system-design'
  | 'login_only'
  | 'practice_question'
  | 'pricing_pack'

export type LoginMethod = 'google' | 'email_otp' | 'github'

export type LoginFailureStep =
  | 'send_otp'
  | 'verify_otp'
  | 'google'
  | 'github'
  | 'client_validation'

const LANDING_LOGIN_ENTRY_STORAGE_KEY = 'landing_login_entry'

const LANDING_LOGIN_ENTRIES: LandingLoginEntry[] = [
  'hero',
  'interview_formats',
  'pricing',
  'navbar',
  'practice_questions',
  'free_mock_interview_hero',
  'free_mock_interview_offer',
  'free_mock_interview_final',
  'unknown',
]

function parseLandingLoginEntry(raw: string | null): LandingLoginEntry {
  if (raw && (LANDING_LOGIN_ENTRIES as string[]).includes(raw)) {
    return raw as LandingLoginEntry
  }
  return 'unknown'
}

function analyticsPagePath(): string {
  if (typeof window === 'undefined') return '/'
  const { pathname, search } = window.location
  return pathname + search
}

function truncateErrorMessage(message: string, maxLen = 100): string {
  const trimmed = message.trim()
  if (trimmed.length <= maxLen) return trimmed
  return trimmed.slice(0, maxLen)
}

type AuthEventBase = {
  method: LoginMethod
  login_entry?: LandingLoginEntry
}

/** Persist login entry for OAuth redirects (GitHub) that leave the landing modal. */
export function persistLandingLoginEntry(entry: LandingLoginEntry): void {
  if (typeof window === 'undefined') return
  try {
    sessionStorage.setItem(LANDING_LOGIN_ENTRY_STORAGE_KEY, entry)
  } catch {
    // ignore
  }
}

export function peekLandingLoginEntry(): LandingLoginEntry {
  if (typeof window === 'undefined') return 'unknown'
  try {
    return parseLandingLoginEntry(
      sessionStorage.getItem(LANDING_LOGIN_ENTRY_STORAGE_KEY),
    )
  } catch {
    return 'unknown'
  }
}

export function consumeLandingLoginEntry(): LandingLoginEntry {
  if (typeof window === 'undefined') return 'unknown'
  try {
    const entry = parseLandingLoginEntry(
      sessionStorage.getItem(LANDING_LOGIN_ENTRY_STORAGE_KEY),
    )
    sessionStorage.removeItem(LANDING_LOGIN_ENTRY_STORAGE_KEY)
    return entry
  } catch {
    return 'unknown'
  }
}

function authEventParams(
  base: AuthEventBase,
  extra?: Record<string, unknown>,
): Record<string, unknown> {
  const loginEntry = base.login_entry ?? peekLandingLoginEntry()
  return {
    method: base.method,
    login_entry: loginEntry,
    page_path: analyticsPagePath(),
    ...extra,
  }
}

/**
 * Track a primary CTA click on the homepage (logged-in navigation or login modal open).
 */
export function landingCtaClick(params: {
  cta_surface: LandingCtaSurface
  journey: LandingCtaJourney
}): void {
  event('landing_cta_click', {
    cta_surface: params.cta_surface,
    journey: params.journey,
    page_path: analyticsPagePath(),
  })
}

/**
 * Auth events are followed by a redirect often enough (post-login navigation, OAuth
 * hand-off) that they all use delivery-aware sending. Await the returned promise
 * before navigating away; ignoring it still sends over `beacon` transport.
 */
export function loginAttempt(base: AuthEventBase): Promise<void> {
  return eventWithDelivery('login_attempt', authEventParams(base))
}

export function loginSuccess(base: AuthEventBase): Promise<void> {
  return eventWithDelivery('login_success', authEventParams(base))
}

export function loginFailure(
  base: AuthEventBase & {
    failure_step: LoginFailureStep
    error_message: string
  },
): Promise<void> {
  return eventWithDelivery(
    'login_failure',
    authEventParams(base, {
      failure_step: base.failure_step,
      error_message: truncateErrorMessage(base.error_message),
    }),
  )
}

/**
 * Signup funnel for the landing login modal (email OTP) and OAuth when the API
 * reports a new account. Await before navigating away, same as login helpers.
 */
export function signUpStart(base: AuthEventBase): Promise<void> {
  return eventWithDelivery('sign_up_start', authEventParams(base))
}

export function signUpSuccessful(base: AuthEventBase): Promise<void> {
  return eventWithDelivery('sign_up_successful', authEventParams(base))
}

export function signUpFailure(
  base: AuthEventBase & {
    failure_step: LoginFailureStep
    error_message: string
  },
): Promise<void> {
  return eventWithDelivery(
    'sign_up_failure',
    authEventParams(base, {
      failure_step: base.failure_step,
      error_message: truncateErrorMessage(base.error_message),
    }),
  )
}

export { GA_MEASUREMENT_ID }
